/**
 * ChatAgent — Floating AI assistant widget
 * Knows all about Pawan Kumar's portfolio, projects, skills, and contact info.
 * Works fully client-side — no backend or API key required.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { MessageSquare, X, Send, Bot, User, Sparkles, ChevronDown } from 'lucide-react'

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  bg:        '#0a0a0f',
  surface:   '#12121a',
  surface2:  '#1a1a26',
  surface3:  '#22223a',
  border:    'rgba(255,255,255,0.07)',
  primary:   '#6c63ff',
  secondary: '#00d4ff',
  accent:    '#ff6b6b',
  success:   '#00e5a0',
  text:      '#e2e8f0',
  textMuted: '#94a3b8',
  textSubtle:'#475569',
}

// ─── Knowledge base ────────────────────────────────────────────────────────────
const KB = {
  greeting: [
    "Namaste! Main Pawan ka AI assistant hoon. Main aapko Pawan ke projects, skills aur contact ke baare mein bata sakta hoon. Kya jaanna chahte ho?",
    "Hi there! I'm Pawan's AI portfolio assistant. Ask me anything about his projects, skills, or how to get in touch!",
  ],

  about: `Pawan Kumar ek passionate BCA student hai jo Cyber Security mein specialise kar raha hai Shoolini University se. Woh real-world apps banata hai jo everyday problems solve karte hain — women's safety se lekar hostel management tak. Currently Python, HTML/CSS aur AI tools explore kar raha hai better products banane ke liye.\n\n📍 Nuh, Haryana, India — 122107\n📧 pawankumar1621998@gmail.com\n📞 +91 9813739244`,

  projects: {
    all: `Pawan ne 5 real-world projects banaye hain:\n\n1. 🛡️ **HerWay** — Women safety app with SOS alerts & safe routes\n2. 💌 **LoveVault** — Private memory vault for couples\n3. 🏠 **SmartPG** — PG/hostel management system\n4. 💰 **PriceRadar** — Real-time price comparison tool\n5. 🗺️ **DelhiExplore** — Delhi tourism & local guide app\n\nKisi specific project ke baare mein jaanna hai?`,
    herway: `🛡️ **HerWay — Women Safety App**\n\nEk emergency safety application jo women ko protect karta hai:\n• One-tap SOS alert system\n• Safe route navigation\n• Real-time location sharing with trusted contacts\n• Nearby safe zones & police stations\n\nPawan ne isse ek real social problem solve karne ke liye banaya — women ki safety ko improve karna.`,
    lovevault: `💌 **LoveVault — Private Memory Vault**\n\nCouples ke liye ek private digital space:\n• Shared photo & video memories\n• Private love letters & notes\n• Anniversary & milestone reminders\n• End-to-end encrypted content\n\nLoveVault couples ko apne special moments safely store karne deta hai.`,
    smartpg: `🏠 **SmartPG — Hostel Management System**\n\nPG aur hostel owners ke liye complete management solution:\n• Tenant registration & room allocation\n• Rent tracking & payment reminders\n• Maintenance request system\n• Owner dashboard with analytics\n\nSmartPG ne hostel management ko paperless aur efficient banaya hai.`,
    priceradar: `💰 **PriceRadar — Price Comparison Tool**\n\nSmart shopping ke liye real-time price tracker:\n• Multi-platform price comparison\n• Price drop alerts & notifications\n• Historical price charts\n• Best deal recommendations\n\nPriceRadar users ko paisa bachane mein help karta hai by comparing prices across platforms.`,
    delhiexplore: `🗺️ **DelhiExplore — Delhi Tourism Guide**\n\nDelhi visitors ke liye comprehensive local guide:\n• Hidden gems & popular spots\n• Local food & restaurant recommendations\n• Budget-friendly travel routes\n• Hindi & English bilingual support\n\nDelhiExplore tourists ko authentic Delhi experience discover karne mein help karta hai.`,
  },

  skills: `Pawan ke current skills:\n\n💻 **Technical:**\n• HTML/CSS — 60%\n• Python — 40%\n• JavaScript — 30%\n• Linux Basics — 45%\n\n🔧 **Tools & Others:**\n• ChatGPT / AI Tools — 80%\n• SEO Basics — 50%\n• Computer Operations — 85%\n\nWoh actively zyada skills seekh raha hai!`,

  education: `🎓 **Pawan Ki Education:**\n\nDegree: BCA (Bachelor of Computer Applications)\nSpecialisation: Cyber Security\nUniversity: Shoolini University\nYear Started: August 2023\n\nCyber Security mein focus ke saath woh ethical hacking, network security aur secure app development seekh raha hai.`,

  contact: `📬 **Pawan Se Contact Karo:**\n\n📧 Email: pawankumar1621998@gmail.com\n📞 Phone: +91 9813739244\n📍 Location: Nuh, Haryana, India — 122107\n🌐 Website: pawan.dev\n\n**Social:**\n• GitHub: github.com/pawankumar\n• LinkedIn: pawan-kumar-bca\n\nProject ya collaboration ke liye directly email karo — woh usually 24 hours mein respond karta hai!`,

  hire: `💼 **Pawan Ko Hire Karo:**\n\nPawan available hai:\n• Freelance web development projects\n• App development collaborations\n• Internship opportunities (Cyber Security / Dev)\n• Open source contributions\n\nUske paas 5 real-world projects ka practical experience hai aur woh passionate hai problem-solving ke baare mein.\n\n📧 pawankumar1621998@gmail.com`,

  fallback: [
    "Hmm, main samjha nahi. Aap yeh puch sakte ho:\n• Projects ke baare mein\n• Skills ke baare mein\n• Contact/hire kaise karein\n• Education ke baare mein",
    "Main sure nahi hoon main kya answer doon. Try karo:\n• 'Tell me about HerWay'\n• 'What skills does Pawan have?'\n• 'How can I contact Pawan?'",
  ],
}

// ─── Quick reply chips ────────────────────────────────────────────────────────
const QUICK_REPLIES = [
  { label: 'Projects dikhao', key: 'projects' },
  { label: 'Skills kya hain?', key: 'skills' },
  { label: 'Contact karo', key: 'contact' },
  { label: 'Hire karna hai', key: 'hire' },
]

// ─── Response engine ──────────────────────────────────────────────────────────
function getResponse(input) {
  const msg = input.toLowerCase().trim()

  // Greeting
  if (/^(hi|hello|hey|namaste|helo|hii|namasthe|helo|sup|yo)\b/.test(msg)) {
    return KB.greeting[1]
  }

  // About / Who
  if (/who|kaun|about|pawan|introduce|yourself|apne|baro|btao|batao/.test(msg)) {
    return KB.about
  }

  // Specific projects
  if (/herway|her way|women|safety|sos|ladki|mahila/.test(msg)) return KB.projects.herway
  if (/lovevault|love vault|couple|memory|memories|pyar|love/.test(msg)) return KB.projects.lovevault
  if (/smartpg|smart pg|hostel|pg |tenant|rent|room/.test(msg)) return KB.projects.smartpg
  if (/priceradar|price radar|price|shopping|compare|deal|paisa/.test(msg)) return KB.projects.priceradar
  if (/delhiexplore|delhi explore|delhi|tourism|travel|guide/.test(msg)) return KB.projects.delhiexplore

  // All projects
  if (/project|app|build|bana|kaam|work|portfolio/.test(msg)) return KB.projects.all

  // Skills
  if (/skill|technology|tech|stack|language|coding|python|html|css|javascript|linux|seo|ai tool/.test(msg)) {
    return KB.skills
  }

  // Education
  if (/education|study|college|university|degree|bca|cyber|padhai|padh|shoolini/.test(msg)) {
    return KB.education
  }

  // Hire
  if (/hire|job|internship|freelance|work with|collaborate|opportunity|employ/.test(msg)) {
    return KB.hire
  }

  // Contact
  if (/contact|email|phone|number|reach|milna|milein|connect|touch|message|mail/.test(msg)) {
    return KB.contact
  }

  // Thanks
  if (/thank|thanks|shukriya|dhanyawad|theek hai|ok thanks|great/.test(msg)) {
    return "Shukriya! Koi aur sawaal ho toh zaroor puchho. Pawan ke portfolio mein aapka swagat hai! "
  }

  // Fallback
  return KB.fallback[Math.floor(Math.random() * KB.fallback.length)]
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatTime(date) {
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
}

function parseMarkdown(text) {
  // Bold: **text**
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong style="color:#e2e8f0;font-weight:700">$1</strong>')
    .replace(/\n/g, '<br/>')
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function TypingDots() {
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', padding: '4px 2px' }}>
      {[0, 1, 2].map(i => (
        <motion.div
          key={i}
          animate={{ y: [0, -5, 0] }}
          transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }}
          style={{ width: 7, height: 7, borderRadius: '50%', background: C.primary }}
        />
      ))}
    </div>
  )
}

function BotMessage({ text, isNew }) {
  return (
    <motion.div
      initial={isNew ? { opacity: 0, x: -12, scale: 0.95 } : false}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      transition={{ duration: 0.25 }}
      style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}
    >
      {/* Bot avatar */}
      <div style={{
        flexShrink: 0, width: 28, height: 28, borderRadius: '50%',
        background: `linear-gradient(135deg, ${C.primary}, ${C.secondary})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 0 12px ${C.primary}44`,
        marginTop: 2,
      }}>
        <Bot size={14} color="#fff" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          background: C.surface2,
          border: `1px solid rgba(108,99,255,0.2)`,
          borderRadius: '4px 14px 14px 14px',
          padding: '10px 13px',
          fontSize: '0.82rem',
          color: C.text,
          lineHeight: 1.65,
          wordBreak: 'break-word',
        }}
          dangerouslySetInnerHTML={{ __html: parseMarkdown(text) }}
        />
      </div>
    </motion.div>
  )
}

function UserMessage({ text, time, isNew }) {
  return (
    <motion.div
      initial={isNew ? { opacity: 0, x: 12, scale: 0.95 } : false}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      transition={{ duration: 0.22 }}
      style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'flex-start' }}
    >
      <div style={{ maxWidth: '78%' }}>
        <div style={{
          background: `linear-gradient(135deg, ${C.primary}cc, ${C.secondary}99)`,
          borderRadius: '14px 4px 14px 14px',
          padding: '10px 13px',
          fontSize: '0.82rem',
          color: '#fff',
          lineHeight: 1.6,
          wordBreak: 'break-word',
        }}>
          {text}
        </div>
        <div style={{ fontSize: '0.68rem', color: C.textSubtle, marginTop: 3, textAlign: 'right' }}>
          {time}
        </div>
      </div>
      {/* User avatar */}
      <div style={{
        flexShrink: 0, width: 28, height: 28, borderRadius: '50%',
        background: C.surface3,
        border: `1px solid ${C.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginTop: 2,
      }}>
        <User size={13} color={C.textMuted} />
      </div>
    </motion.div>
  )
}

// ─── Main ChatAgent component ─────────────────────────────────────────────────
export default function ChatAgent() {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState([
    { id: 0, role: 'bot', text: KB.greeting[0], time: formatTime(new Date()), isNew: false },
  ])
  const [input, setInput] = useState('')
  const [typing, setTyping] = useState(false)
  const [unread, setUnread] = useState(1)
  const [showScrollBtn, setShowScrollBtn] = useState(false)
  const messagesEndRef = useRef(null)
  const scrollAreaRef = useRef(null)
  const inputRef = useRef(null)

  // Auto-scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    if (open) {
      scrollToBottom()
      setUnread(0)
      setTimeout(() => inputRef.current?.focus(), 350)
    }
  }, [open, scrollToBottom])

  useEffect(() => {
    if (open) scrollToBottom()
  }, [messages, typing, open, scrollToBottom])

  // Detect scroll-up in chat
  function handleScroll() {
    const el = scrollAreaRef.current
    if (!el) return
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60
    setShowScrollBtn(!atBottom)
  }

  // Send a message
  function sendMessage(text) {
    const trimmed = (text || input).trim()
    if (!trimmed) return

    const userMsg = { id: Date.now(), role: 'user', text: trimmed, time: formatTime(new Date()), isNew: true }
    setMessages(prev => [...prev, userMsg])
    setInput('')
    setTyping(true)

    // Simulate AI thinking delay (600–1200ms)
    const delay = 600 + Math.random() * 600
    setTimeout(() => {
      const response = getResponse(trimmed)
      const botMsg = { id: Date.now() + 1, role: 'bot', text: response, time: formatTime(new Date()), isNew: true }
      setMessages(prev => [...prev, botMsg])
      setTyping(false)
      if (!open) setUnread(n => n + 1)
    }, delay)
  }

  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  return (
    <>
      {/* ── Chat panel ── */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.92 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            style={{
              position: 'fixed',
              bottom: 90,
              right: 20,
              zIndex: 9998,
              width: 'min(370px, calc(100vw - 32px))',
              height: 'min(560px, calc(100vh - 120px))',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 20,
              overflow: 'hidden',
              background: C.surface,
              border: `1px solid rgba(108,99,255,0.25)`,
              boxShadow: `0 24px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(108,99,255,0.1), inset 0 1px 0 rgba(255,255,255,0.05)`,
              fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
            }}
          >
            {/* Header */}
            <div style={{
              flexShrink: 0,
              padding: '14px 16px',
              background: `linear-gradient(135deg, rgba(108,99,255,0.18) 0%, rgba(0,212,255,0.08) 100%)`,
              borderBottom: `1px solid ${C.border}`,
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              {/* Bot avatar with pulse */}
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: '50%',
                  background: `linear-gradient(135deg, ${C.primary}, ${C.secondary})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: `0 0 16px ${C.primary}55`,
                }}>
                  <Bot size={18} color="#fff" />
                </div>
                <div style={{
                  position: 'absolute', bottom: 1, right: 1,
                  width: 9, height: 9, borderRadius: '50%',
                  background: '#00e5a0',
                  border: `1.5px solid ${C.surface}`,
                }} />
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.88rem', fontWeight: 700, color: C.text }}>
                  Pawan's AI Assistant
                </div>
                <div style={{ fontSize: '0.7rem', color: '#00e5a0', marginTop: 1 }}>
                  Online — Ready to help
                </div>
              </div>

              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => setOpen(false)}
                style={{
                  width: 30, height: 30, borderRadius: 8, border: `1px solid ${C.border}`,
                  background: 'rgba(255,255,255,0.04)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: C.textMuted, flexShrink: 0,
                }}
              >
                <X size={15} />
              </motion.button>
            </div>

            {/* Messages area */}
            <div
              ref={scrollAreaRef}
              onScroll={handleScroll}
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '14px 12px',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                scrollbarWidth: 'thin',
                scrollbarColor: `${C.surface3} transparent`,
              }}
            >
              {messages.map((msg) =>
                msg.role === 'bot'
                  ? <BotMessage key={msg.id} text={msg.text} isNew={msg.isNew} />
                  : <UserMessage key={msg.id} text={msg.text} time={msg.time} isNew={msg.isNew} />
              )}

              {/* Typing indicator */}
              <AnimatePresence>
                {typing && (
                  <motion.div
                    key="typing"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0 }}
                    style={{ display: 'flex', gap: 8, alignItems: 'center' }}
                  >
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%',
                      background: `linear-gradient(135deg, ${C.primary}, ${C.secondary})`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Bot size={14} color="#fff" />
                    </div>
                    <div style={{
                      background: C.surface2,
                      border: `1px solid rgba(108,99,255,0.2)`,
                      borderRadius: '4px 14px 14px 14px',
                      padding: '8px 12px',
                    }}>
                      <TypingDots />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div ref={messagesEndRef} />
            </div>

            {/* Scroll-to-bottom button */}
            <AnimatePresence>
              {showScrollBtn && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  onClick={scrollToBottom}
                  style={{
                    position: 'absolute',
                    bottom: 120, right: 12,
                    width: 30, height: 30, borderRadius: '50%',
                    background: C.surface2,
                    border: `1px solid ${C.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: C.textMuted, zIndex: 2,
                  }}
                >
                  <ChevronDown size={15} />
                </motion.button>
              )}
            </AnimatePresence>

            {/* Quick replies */}
            <div style={{
              flexShrink: 0,
              padding: '8px 12px 4px',
              display: 'flex',
              gap: 6,
              flexWrap: 'wrap',
              borderTop: `1px solid ${C.border}`,
            }}>
              {QUICK_REPLIES.map(qr => (
                <motion.button
                  key={qr.key}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.96 }}
                  onClick={() => sendMessage(qr.label)}
                  style={{
                    padding: '5px 10px',
                    borderRadius: 20,
                    border: `1px solid rgba(108,99,255,0.35)`,
                    background: 'rgba(108,99,255,0.08)',
                    color: C.primary,
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(108,99,255,0.18)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(108,99,255,0.08)'}
                >
                  {qr.label}
                </motion.button>
              ))}
            </div>

            {/* Input bar */}
            <div style={{
              flexShrink: 0,
              padding: '8px 12px 12px',
              display: 'flex',
              gap: 8,
              alignItems: 'center',
            }}>
              <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                background: C.surface2,
                border: `1px solid ${C.border}`,
                borderRadius: 12,
                padding: '0 12px',
                transition: 'border-color 0.2s',
              }}
                onFocus={e => e.currentTarget.style.borderColor = `${C.primary}60`}
                onBlur={e => e.currentTarget.style.borderColor = C.border}
              >
                <input
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKey}
                  placeholder="Kuch bhi puchho..."
                  maxLength={300}
                  style={{
                    flex: 1,
                    background: 'none',
                    border: 'none',
                    outline: 'none',
                    color: C.text,
                    fontSize: '0.82rem',
                    padding: '10px 0',
                    fontFamily: 'inherit',
                  }}
                />
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.92 }}
                onClick={() => sendMessage()}
                disabled={!input.trim()}
                style={{
                  width: 40, height: 40, borderRadius: 12, border: 'none',
                  background: input.trim()
                    ? `linear-gradient(135deg, ${C.primary}, ${C.secondary})`
                    : C.surface2,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: input.trim() ? 'pointer' : 'default',
                  flexShrink: 0,
                  transition: 'background 0.2s',
                  boxShadow: input.trim() ? `0 4px 14px ${C.primary}44` : 'none',
                }}
              >
                <Send size={16} color={input.trim() ? '#fff' : C.textSubtle} />
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Floating trigger button ── */}
      <motion.button
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.93 }}
        onClick={() => { setOpen(v => !v); setUnread(0) }}
        aria-label={open ? 'Close chat' : 'Open AI assistant'}
        style={{
          position: 'fixed',
          bottom: 22,
          right: 20,
          zIndex: 9999,
          width: 56, height: 56,
          borderRadius: '50%',
          border: 'none',
          background: open
            ? C.surface2
            : `linear-gradient(135deg, ${C.primary}, ${C.secondary})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: open
            ? `0 4px 20px rgba(0,0,0,0.4)`
            : `0 4px 24px ${C.primary}66, 0 0 0 0 ${C.primary}44`,
          transition: 'background 0.3s, box-shadow 0.3s',
        }}
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.div key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <X size={22} color="#e2e8f0" />
            </motion.div>
          ) : (
            <motion.div key="open"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <MessageSquare size={22} color="#fff" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Unread badge */}
        <AnimatePresence>
          {!open && unread > 0 && (
            <motion.div
              key="badge"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              style={{
                position: 'absolute', top: -2, right: -2,
                width: 18, height: 18, borderRadius: '50%',
                background: '#ff6b6b',
                border: `2px solid #0a0a0f`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.6rem', fontWeight: 800, color: '#fff',
              }}
            >
              {unread}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Pulse ring (only when closed) */}
        {!open && (
          <motion.div
            animate={{ scale: [1, 1.6], opacity: [0.5, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
            style={{
              position: 'absolute', inset: 0, borderRadius: '50%',
              background: `${C.primary}40`,
              pointerEvents: 'none',
            }}
          />
        )}
      </motion.button>
    </>
  )
}
